import { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stepper,
  Step,
  StepLabel,
  Box,
  Slider,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Paper,
  CircularProgress,
} from '@mui/material';
import {
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import type { Risk, CreateRiskDto, UpdateRiskDto } from 'shared';
import { RiskStatus, RiskTreatment } from 'shared';
import { riskApi } from '../services/risk.service';

// =============================================================================
// TYPES
// =============================================================================

interface RiskFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateRiskDto | UpdateRiskDto) => Promise<void>;
  componentId: string;
  spaceId: string;
  initialData?: Risk;
  mode: 'create' | 'edit';
}

interface RiskFormData {
  // Step 1: Identification
  title: string;
  description: string;
  threatSource: string;
  vulnerability: string;
  existingControls: string[];

  // Step 2: Assessment
  confidentialityImpact: number;
  integrityImpact: number;
  availabilityImpact: number;
  likelihood: number;

  // Step 3: Treatment
  riskTreatment: RiskTreatment;
  owner: string;
  reviewDate: string;
}

// =============================================================================
// CONSTANTS
// =============================================================================

const STEPS = ['step1', 'step2', 'step3'];

const DEFAULT_VALUES: RiskFormData = {
  title: '',
  description: '',
  threatSource: '',
  vulnerability: '',
  existingControls: [],
  confidentialityImpact: 5,
  integrityImpact: 5,
  availabilityImpact: 5,
  likelihood: 5,
  riskTreatment: RiskTreatment.MITIGATE,
  owner: '',
  reviewDate: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000)
    .toISOString()
    .split('T')[0], // 90 days from now
};

const RISK_TREATMENT_OPTIONS: RiskTreatment[] = [
  RiskTreatment.MITIGATE,
  RiskTreatment.ACCEPT,
  RiskTreatment.TRANSFER,
  RiskTreatment.AVOID,
];

// =============================================================================
// HELPER COMPONENTS
// =============================================================================

interface RiskLevelIndicatorProps {
  score: number;
}

function RiskLevelIndicator({ score }: RiskLevelIndicatorProps) {
  const { t } = useTranslation();
  const level = riskApi.getRiskLevel(score);
  const color = riskApi.getRiskColor(score);

  const icons = {
    critical: <ErrorIcon />,
    high: <WarningIcon />,
    medium: <WarningIcon />,
    low: <CheckCircleIcon />,
    minimal: <CheckCircleIcon />,
  };

  return (
    <Paper
      elevation={2}
      sx={{
        p: 2,
        mt: 2,
        borderLeft: `4px solid ${color}`,
        backgroundColor: `${color}10`,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ color }}>{icons[level]}</Box>
        <Box>
          <Typography variant="subtitle2">
            {t('risk.scores.inherent')}
          </Typography>
          <Typography variant="h6" sx={{ color }}>
            {score.toFixed(2)}/10 - {t(`risk.level.${level}`).toUpperCase()}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
}

interface SliderFieldProps {
  name: keyof RiskFormData;
  label: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: any;
  min?: number;
  max?: number;
}

function SliderField({
  name,
  label,
  control,
  min = 1,
  max = 10,
}: SliderFieldProps) {
  const { t } = useTranslation();

  const marks = [
    { value: 1, label: '1' },
    { value: 5, label: '5' },
    { value: 10, label: '10' },
  ];

  const getSliderLabel = (value: number): string => {
    if (value <= 2) return t('risk.level.minimal');
    if (value <= 4) return t('risk.level.low');
    if (value <= 6) return t('risk.level.medium');
    if (value <= 8) return t('risk.level.high');
    return t('risk.level.critical');
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <Box sx={{ mb: 3 }}>
          <Typography gutterBottom>
            {label}: {field.value}/10 - {getSliderLabel(field.value as number)}
          </Typography>
          <Slider
            {...field}
            min={min}
            max={max}
            step={1}
            marks={marks}
            valueLabelDisplay="auto"
            onChange={(_, value) => field.onChange(value)}
          />
        </Box>
      )}
    />
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function RiskForm({
  open,
  onClose,
  onSubmit,
  componentId,
  spaceId,
  initialData,
  mode,
}: RiskFormProps) {
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newControl, setNewControl] = useState('');

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<RiskFormData>({
    defaultValues: DEFAULT_VALUES,
  });

  // Reset form when initialData changes (for edit mode)
  useEffect(() => {
    if (open && initialData) {
      reset({
        title: initialData.title,
        description: initialData.description,
        threatSource: initialData.threatSource,
        vulnerability: initialData.vulnerability,
        existingControls: initialData.existingControls || [],
        confidentialityImpact: initialData.confidentialityImpact,
        integrityImpact: initialData.integrityImpact,
        availabilityImpact: initialData.availabilityImpact,
        likelihood: initialData.likelihood,
        riskTreatment: initialData.riskTreatment,
        owner: initialData.owner,
        reviewDate: initialData.reviewDate
          ? new Date(initialData.reviewDate).toISOString().split('T')[0]
          : DEFAULT_VALUES.reviewDate,
      });
      setActiveStep(0);
    } else if (open && !initialData) {
      reset(DEFAULT_VALUES);
      setActiveStep(0);
    }
  }, [open, initialData, reset]);

  const watchedValues = watch();

  // Calculate inherent risk based on current values (with fallbacks for undefined)
  const inherentRisk = riskApi.calculateInherentRisk(
    watchedValues.confidentialityImpact ?? DEFAULT_VALUES.confidentialityImpact,
    watchedValues.integrityImpact ?? DEFAULT_VALUES.integrityImpact,
    watchedValues.availabilityImpact ?? DEFAULT_VALUES.availabilityImpact,
    watchedValues.likelihood ?? DEFAULT_VALUES.likelihood
  );

  const riskLevel = riskApi.getRiskLevel(inherentRisk);

  // Handle step navigation
  const handleNext = () => {
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  // Handle adding existing controls
  const handleAddControl = () => {
    if (newControl.trim()) {
      const currentControls = watchedValues.existingControls || [];
      setValue('existingControls', [...currentControls, newControl.trim()]);
      setNewControl('');
    }
  };

  const handleRemoveControl = (index: number) => {
    const currentControls = watchedValues.existingControls || [];
    setValue(
      'existingControls',
      currentControls.filter((_, i) => i !== index)
    );
  };

  // Handle form submission
  const handleFormSubmit = async (data: RiskFormData) => {
    setIsSubmitting(true);
    try {
      const riskData: CreateRiskDto | UpdateRiskDto = {
        title: data.title,
        description: data.description,
        threatSource: data.threatSource,
        vulnerability: data.vulnerability,
        existingControls: data.existingControls,
        confidentialityImpact: data.confidentialityImpact,
        integrityImpact: data.integrityImpact,
        availabilityImpact: data.availabilityImpact,
        likelihood: data.likelihood,
        riskTreatment: data.riskTreatment,
        owner: data.owner,
        reviewDate: new Date(data.reviewDate),
        status: mode === 'create' ? RiskStatus.IDENTIFIED : undefined,
        ...(mode === 'create' && { componentId, spaceId }),
      };

      await onSubmit(riskData);
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get recommendation text based on risk level
  const getRecommendation = () => {
    if (riskLevel === 'critical' || riskLevel === 'high') {
      return {
        severity: 'warning' as const,
        text: t('risk.recommendation.high'),
      };
    }
    if (riskLevel === 'medium') {
      return {
        severity: 'info' as const,
        text: t('risk.recommendation.medium'),
      };
    }
    return { severity: 'success' as const, text: t('risk.recommendation.low') };
  };

  // Render step content
  const renderStepContent = (step: number) => {
    switch (step) {
      case 0: // Identification
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Controller
              name="title"
              control={control}
              rules={{ required: t('validation.required') }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('risk.fields.title')}
                  fullWidth
                  error={!!errors.title}
                  helperText={errors.title?.message}
                />
              )}
            />

            <Controller
              name="description"
              control={control}
              rules={{
                required: t('validation.required'),
                minLength: {
                  value: 20,
                  message: t('validation.minLength', { min: 20 }),
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('risk.fields.description')}
                  fullWidth
                  multiline
                  rows={3}
                  error={!!errors.description}
                  helperText={errors.description?.message}
                />
              )}
            />

            <Controller
              name="threatSource"
              control={control}
              rules={{ required: t('validation.required') }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('risk.fields.threatSource')}
                  fullWidth
                  placeholder={t('risk.fields.threatSourcePlaceholder')}
                  error={!!errors.threatSource}
                  helperText={errors.threatSource?.message}
                />
              )}
            />

            <Controller
              name="vulnerability"
              control={control}
              rules={{ required: t('validation.required') }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('risk.fields.vulnerability')}
                  fullWidth
                  placeholder={t('risk.fields.vulnerabilityPlaceholder')}
                  error={!!errors.vulnerability}
                  helperText={errors.vulnerability?.message}
                />
              )}
            />

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {t('risk.fields.existingControls')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <TextField
                  size="small"
                  value={newControl}
                  onChange={e => setNewControl(e.target.value)}
                  placeholder={t('risk.fields.existingControlsPlaceholder')}
                  fullWidth
                  onKeyPress={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddControl();
                    }
                  }}
                />
                <Button variant="outlined" onClick={handleAddControl}>
                  {t('common.add')}
                </Button>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(watchedValues.existingControls || []).map(
                  (control, index) => (
                    <Chip
                      key={index}
                      label={control}
                      onDelete={() => handleRemoveControl(index)}
                      size="small"
                    />
                  )
                )}
              </Box>
            </Box>
          </Box>
        );

      case 1: // Assessment
        return (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('risk.impact.title')}: {t('risk.likelihood.help')}
            </Typography>

            <SliderField
              name="confidentialityImpact"
              label={t('risk.fields.confidentialityImpact')}
              control={control}
            />

            <SliderField
              name="integrityImpact"
              label={t('risk.fields.integrityImpact')}
              control={control}
            />

            <SliderField
              name="availabilityImpact"
              label={t('risk.fields.availabilityImpact')}
              control={control}
            />

            <SliderField
              name="likelihood"
              label={t('risk.fields.likelihood')}
              control={control}
            />

            <RiskLevelIndicator score={inherentRisk} />
          </Box>
        );

      case 2: // Treatment
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity={getRecommendation().severity}>
              {getRecommendation().text}
            </Alert>

            <Controller
              name="riskTreatment"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel>{t('risk.fields.riskTreatment')}</InputLabel>
                  <Select {...field} label={t('risk.fields.riskTreatment')}>
                    {RISK_TREATMENT_OPTIONS.map(option => (
                      <MenuItem key={option} value={option}>
                        {t(`risk.treatment.${option}`)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />

            <Controller
              name="owner"
              control={control}
              rules={{ required: t('validation.required') }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('risk.fields.owner')}
                  fullWidth
                  error={!!errors.owner}
                  helperText={errors.owner?.message}
                />
              )}
            />

            <Controller
              name="reviewDate"
              control={control}
              rules={{ required: t('validation.required') }}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="date"
                  label={t('risk.fields.reviewDate')}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.reviewDate}
                  helperText={errors.reviewDate?.message}
                />
              )}
            />
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { minHeight: '500px' } }}
    >
      <DialogTitle>
        {mode === 'create' ? t('risk.create') : t('risk.edit')}
      </DialogTitle>

      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ pt: 2, pb: 4 }}>
          {STEPS.map(step => (
            <Step key={step}>
              <StepLabel>{t(`risk.wizard.${step}`)}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <form onSubmit={handleSubmit(handleFormSubmit)}>
          {renderStepContent(activeStep)}
        </form>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={isSubmitting}>
          {t('common.cancel')}
        </Button>

        <Box sx={{ flex: 1 }} />

        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={isSubmitting}>
            {t('common.back')}
          </Button>
        )}

        {activeStep < STEPS.length - 1 ? (
          <Button variant="contained" onClick={handleNext}>
            {t('common.next')}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleSubmit(handleFormSubmit)}
            disabled={isSubmitting}
          >
            {isSubmitting ? <CircularProgress size={24} /> : t('common.save')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default RiskForm;
